/* eslint-disable react/prop-types */
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
// api
import ApiKey from '../../../Components/api/ApiKey';
import BaseApi from '../../../Components/api/BaseApi';

import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { GoogleAnalyticsService } from 'core/services/GoogleAnalyticsService';
import { Box, Button } from '@radix-ui/themes';
import { useApplyJobCtx } from '../apply-job-provider';
import { ResumeSelector } from './resume-selector';
import { observer } from 'mobx-react-lite';

const ApplyJobSelectForm = ({
   jobId,
   setStep,
   onlyResume,
   onlyQuestionnaire,
   subscribeAlert,
   user,
   onApply,
}) => {
   const { t } = useTranslation('global');
   const tokenKey = Cookies.get('tokenClient');

   const applyJobStore = useApplyJobCtx();
   const resumeChooserStore = applyJobStore.resumeChooserStore;

   useEffect(() => {
      resumeChooserStore.loadCvLists();
   }, []);

   const [file, setFile] = useState(null);
   const [coverLetter, setCoverLetter] = useState('');
   const [errors, setErrors] = useState({ file: '' });

   const handleFileChange = (file) => {
      GoogleAnalyticsService.trackEvent('job_apply-upload-file', {
         message: t('jobDescription.applyError'),
         status: 'ERROR',
      });
      const allowedTypes = [
         'text/plain',
         'application/pdf',
         'application/msword',
         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      if (file && file.size <= 5 * 1024 * 1024 && allowedTypes.includes(file.type)) {
         setFile(file);
         resumeChooserStore.setFile(file);
         GoogleAnalyticsService.trackEvent('job_apply-upload-file', {
            message: 'File uploaded successfully',
            status: 'SUCCESS',
         });
         setErrors((prev) => ({
            ...prev,
            file: '',
         }));
      } else {
         setFile(null);
         setSelectedFileId(null);
         setSelectedResumeId(null);

         setErrors((prev) => ({
            ...prev,
            file: t('jobDescription.fileError'),
         }));
         GoogleAnalyticsService.trackEvent('job_apply-upload-file', {
            message: t('jobDescription.fileError'),
            status: 'ERROR',
         });
      }
   };

   const handleFileDrop = (e) => {
      e.preventDefault();

      const file = e.dataTransfer.files[0];

      handleFileChange(file);
   };

   const handleCoverLetterChange = (e) => {
      const value = e.target.value;

      if (value.length <= 500) {
         setCoverLetter(value);
         resumeChooserStore.setMessage(value)
      }
   };

   const handleFileDownload = async (e, id) => {
      e.preventDefault();

      try {
         const response = await axios.get(BaseApi + `/jobApply/downloadFile/${id}`, {
            headers: {
               key: ApiKey,
               token: tokenKey,
            },
            responseType: 'blob',
         });

         const url = window.URL.createObjectURL(response.data);
         const filename = response.headers['content-disposition'].split('filename=')[1];

         const link = document.createElement('a');
         link.href = url;
         link.setAttribute('download', filename.replace(/['"]/g, ''));
         document.body.appendChild(link);
         link.click();
         window.URL.revokeObjectURL(url);
         document.body.removeChild(link);
      } catch (error) {
         console.error('Could not download file:', error);

         Swal.fire({
            icon: 'error',
            confirmButtonText: t('employerInnerAccountdetail.close'),
            title: t('employerFavouriteListProfile.fileDownloadFailedTitle'),
         });
      }
   };

   return (
      <form
         className="applyJobSelectForm"
         onSubmit={(e) => {
            e.preventDefault();

            applyJobStore.apply(
               {
                  variant: 'cv',
                  idJob: jobId,
                  subscribeAlert,
                  first_name: user.first_name,
                  last_name: user.last_name,
                  email_address: user.email_address,
                  city_id: user.city_id,
                  contact: user.contact,
                  message: coverLetter,
               },
               {
                  onSuccess: (res) => {
                     onApply();
                  },
                  onError: () => {
                     const Toast = Swal.mixin({
                        toast: true,
                        timer: 5000,
                        position: 'top-end',
                        timerProgressBar: true,
                        showConfirmButton: false,
                        didOpen: (toast) => {
                           toast.addEventListener('mouseenter', Swal.stopTimer);
                           toast.addEventListener('mouseleave', Swal.resumeTimer);
                        },
                     });

                     Toast.fire({
                        icon: 'error',
                        title: t('jobDescription.applyError'),
                     });
                  },
               }
            );
         }}
      >
         {onlyQuestionnaire && !onlyResume ? (
            setStep('questionnaire')
         ) : (
            <div>
               {!!onlyResume && (
                  <div>
                     <Box mb="5">
                        <ResumeSelector
                           store={resumeChooserStore}
                           onUploadedFileAction={(event, fileId) =>
                              handleFileDownload(event, fileId)
                           }
                        />
                     </Box>

                     <div className="selectBox">
                        <button
                           type="button"
                           className="selectBoxHeader"
                           onClick={() => setStep('createResume')}
                        >
                           <div className="d-flex align-items-center">
                              <NoteAddRoundedIcon
                                 sx={{ mr: '8px', fontSize: '25px', color: '#294A9C' }}
                              />
                              {t('jobDescription.createResume')}
                           </div>
                        </button>
                     </div>
                     <div className="mb-3">
                        <label className="coverLetterLabel" htmlFor="coverLetter">
                           {t('jobDescription.uploadResume')}
                        </label>
                        <div
                           className="fileUploadBox"
                           onDrop={handleFileDrop}
                           onDragOver={(e) => e.preventDefault()}
                        >
                           <UploadFileRoundedIcon
                              sx={{ mr: '8px', fontSize: '25px', color: '#294A9C' }}
                           />
                           {resumeChooserStore.file ? (
                              <div className="fileUploadItem">
                                 <div>
                                    <p className="fs-6 text-center">{resumeChooserStore.file.name}</p>
                                    <p className="text-center" style={{ color: '#36854D' }}>
                                       {t('jobDescription.fileUploadSuccess')}
                                    </p>
                                 </div>
                                 <div>
                                    <p className="fs-6 fw-bold text-center">
                                       {t('jobDescription.fileUploadTxt1')}{' '}
                                       {t('jobDescription.fileUploadTxt2')}
                                       <label className="fileUploadLabel">
                                          {t('jobDescription.fileUploadTxt4')}
                                          <input
                                             type="file"
                                             className="fileUploadInput"
                                             accept=".doc,.docx,.txt,.pdf"
                                             onChange={(e) => handleFileChange(e.target.files[0])}
                                          />
                                       </label>
                                    </p>
                                    <p className="text-muted">
                                       Підтримуються файли doc, docx, txt, pdf до 5 МБ
                                    </p>
                                 </div>
                              </div>
                           ) : (
                              <div className="fileUploadItem">
                                 <div>
                                    <p className="fs-6 fw-bold text-center">
                                       {t('jobDescription.fileUploadTxt1')}
                                    </p>
                                    <p className="fs-6 fw-bold text-center">
                                       {t('jobDescription.fileUploadTxt2')}
                                       <label className="fileUploadLabel">
                                          {t('jobDescription.fileUploadTxt3')}
                                          <input
                                             type="file"
                                             className="fileUploadInput"
                                             accept=".doc,.docx,.txt,.pdf"
                                             onChange={(e) => handleFileChange(e.target.files[0])}
                                          />
                                       </label>
                                    </p>
                                 </div>
                                 <p className="text-muted text-center">
                                    Підтримуються файли doc, docx, txt, pdf до 5 МБ
                                 </p>
                              </div>
                           )}
                        </div>
                        {errors.file && <div className="text-danger">{errors.file}</div>}
                     </div>
                  </div>
               )}
               {!!onlyResume && !!onlyQuestionnaire && (
                  <div className="selectBox">
                     <button
                        type="button"
                        className="selectBoxHeader"
                        onClick={() => setStep('questionnaire')}
                     >
                        <div className="d-flex align-items-center">
                           <DescriptionRoundedIcon
                              sx={{ mr: '8px', fontSize: '25px', color: '#294A9C' }}
                           />
                           {t('jobDescription.selectQustionnaire')}
                        </div>
                     </button>
                  </div>
               )}
               <div className="coverLetterBox">
                  <label className="coverLetterLabel" htmlFor="coverLetter">
                     {t('jobDescription.coverLetter')}
                  </label>
                  <textarea
                     rows={5}
                     maxLength="500"
                     id="coverLetter"
                     value={coverLetter}
                     className="form-control"
                     onChange={handleCoverLetterChange}
                     placeholder={t('jobDescription.coverLetterPlaceholder')}
                  />
                  <small className="coverLetterCount text-muted">
                     {t('jobDescription.characterLimit')} {500 - coverLetter.length}
                  </small>
               </div>

              
               <div className="subscribeAlertBox">
                  {subscribeAlert}
               </div>
               <div className="dataProcessingConsentBox">
                  <p>
                     {t('jobDescription.bottomTxt1')}{' '}
                     <Link to="/data-processing-consent">{t('jobDescription.bottomTxt2')}</Link>
                  </p>
               </div>
               <div className="d-flex justify-content-evenly">
                  <Button
                     radius="small"
                     size="4"
                     type="submit"
                     loading={applyJobStore.submitting}
                     disabled={!resumeChooserStore.file && !resumeChooserStore.selectedResume}
                  >
                     {t('jobDescription.applyNow')}
                  </Button>
               </div>
            </div>
         )}
      </form>
   );
};

export const ResumeChooserView = observer(ApplyJobSelectForm);
